<template>
  <vp-widget class="vp-message-template" v-bind="{ title, stickyHeader, styles: widgetStyles }">
    <template #title="props">
      <slot name="title" v-bind="props" />
    </template>
    <template #controller>
      <slot name="controller" />
      <vp-tooltip :body="`Preview Mode: ${previewMode ? 'On' : 'Off'}`" :styles="tooltipStyles">
        <template #target>
          <vp-button class="vp-message-template__preview icon-button small blue" :class="{ 'no-fill': !previewMode }" :disabled="disabled" @click="previewMode = !previewMode">
            <i class="fa fa-eye" />
          </vp-button>
        </template>
      </vp-tooltip>
    </template>
    <template #default>
      <div class="vp-message-template__inner" :data-active="!isDisabled">
        <div v-if="Boolean($slots.summary) || showSummary" class="vp-message-template__summary">
          <slot name="summary">
            <p>
              Below you can customize the Subject Line and Body (Body only for SMS) of your templated messages.
              Use the tabs below to navigate through the different types of Message Format Options you selected via <b>Delivery Channels</b>.
            </p>
          </slot>
        </div>
        <VpTabMenu v-show="tabs.length" class="vp-message-template__menu" v-model="tab" :tabs="tabs" :styles="tabStyles" @input="$emit('tab-updated', $event)" />
        <div v-if="tabs.length" class="vp-message-template__content">
          <VpMessageDrafting v-bind="{ ...sharedProps, ...selectedDraft,  }" @input="$emit('input', $event)" />
        </div>
      </div>
    </template>
  </vp-widget>
</template>

<script>
import VpWidget from '../vp-widget/VpWidget.vue';
import VpTabMenu from '../vp-tab-menu/VpTabMenu.vue';
import VpTooltip from '../vp-tooltip/VpTooltip.vue';
import VpSliderToggle from '../vp-slider-toggle/VpSliderToggle.vue';

export default {
  name: 'VpMessageTemplate',
  emits: ['input'],
  components: { VpWidget, VpTabMenu, VpSliderToggle, VpTooltip },
  data() {
    return {
      previewMode: false, 
      tab: this.tabs[0]?.value || '',
    };
  },
  props: {
    stickyHeader: {
      type: Boolean,
      default: false,
    },
    templates: {
      type: Array,
      default: () => [],
    },
    disabled: { 
      type: [Boolean, Number], 
      default: false,
    },
    showSummary: {
      type: Boolean, 
      default: true,
    },
    readonly: { 
      type: Boolean, 
      default: false,
    },
    override: { 
      type: String, 
      default: null,
    },
    title: { 
      type: String, 
      default: 'Message Template',
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    insertResponse: {
      type: String,
      default: ''
    },
  },
  watch: {
    previewMode(bool) {
      return this.$emit('toggle-preview', Boolean(bool));
    },
    tabs(curr, prev) {
      const hasDiff = JSON.stringify(prev) !== JSON.stringify(curr)
      if (hasDiff && !curr.find(({ value }) => value === this.tab)) {
        this.tab = !curr.length ? null : curr[0]?.value;
      }
    },
    override: {
      immediate: true,
      handler(tab) {
        if (tab) {
          this.tab = tab;
        }
      }
    }
  },
  computed: {
    sharedProps() {
      const { insertResponse, tab, previewMode, readonly, disabled } = this;
      return { insertResponse, tab, previewMode, readonly, disabled: Boolean(disabled) };
    },
    selectedDraft() {
      return this.templates.find(({ id }) => id === this.tab);
    },
    isDisabled() {
      return this.disabled || !this.tabs.length;
    },
    widgetStyles() {
      const options = {
        height: '100%',
        innerPadding: '0',
        headerHeight: '100%',
        innerOverflow: 'visible',
        headerColor: '#4F4F4F',
      };

      return Object.assign(options, this.styles);
    },
    tabStyles() {
      const { tab = {} } = this.styles;
      return Object.assign({ 
        activeColor: '#106699', 
        activeUnderlineColor: '#106699', 
        backgroundColor: '#FFFFFF',
        menuPadding: '0 0 0 1.5rem',
        menuBoxShadow: 'none',
      }, tab);
    },
    tooltipStyles() {
      return { fontSize: '12px' };
    }
  }
}
</script>

<style lang="scss" scoped>
  .vp-message-template {
    &__title {
      color: #4F4F4F;
    }
    &__summary, &__content {
      padding: 1.5rem;
    }
    &__summary {
      text-align: left;
    }
    &__inner {
      max-height: 0;
      overflow: hidden;
      transition: max-height 300ms ease-in-out;
      &[data-active] {
        max-height: 1000px;
      }
    }
    &__content {
      border-top: 1px solid #DBDBDB;
    }
    &__preview {
      width: 35px;
    }
    .icon-button {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: unset !important;
      &:hover {
        transform: scale(1.05);
      }
    }
    &::v-deep .vcl-slider-toggle__container {
      /** Slider Toggle Override */
      margin-left: 0 !important;
    }
  }
</style>